import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {ModuleItem} from '../../../shared/models/app-template/module';
import {Option, OptionGroup} from '../../../shared/models/option';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {TileFormat, TileId} from '../../../shared/models/tile';
import {EMPTY_ID} from '../../../shared/constants/empty-id';
import {PopoverMenuComponent} from '../popover-menu/popover-menu.component';
import {PopoverSelectTilesComponent} from '../popover-multi-select/object-types/tiles/popover-select-tiles.component';
import {TileEditorComponent} from '../tile-editor/tile-editor.component';

export type TileSelectorButtonCallback = () => void;

@Component({
  selector: 'motn-tile-selector-button',
  templateUrl: './tile-selector-button.component.html',
  styleUrls: ['./tile-selector-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PopoverMenuComponent,
    PopoverSelectTilesComponent,
    TileEditorComponent,
  ],
})
export class TileSelectorButtonComponent implements OnInit {
  @Input() callbackCreateTile: () => void;
  @Input() formatRestriction: TileFormat;
  @Input() moduleItem: ModuleItem;
  @Input() label: string = '';
  @Input() infoDialogContent: {
    header: string,
    message: string,
  } = {
    header: `What Is a Tile?`,
    message: `Tiles are like individual data points (e.g. a set point for a thermostat), but better.\n\nTiles represent both read-only and writable data points that exist in devices connected to Motion Hubs. When you choose a data point for this setting, Motion creates a tile automatically and associates it with this setting.\n\nDepending on the use case, Motion will add various features to each tile.`,
  };

  menuOpen: boolean = false;
  menuOptions: OptionGroup;

  @Output() clickedExistingTile$: EventEmitter<TileId> = new EventEmitter<TileId>();

  editingTile: TileId = EMPTY_ID;

  existingTilesMenuOpen: boolean = false;


  constructor(
    private shadeTools: ShadeToolsService,
  ) {
  }


  ngOnInit(): void {
    this.menuOptions = this.makeTileMenuOptions();
  }


  openMenu(): void {
    this.menuOpen = true;
  }


  closeMenu(): void {
    this.menuOpen = false;
  }


  infoDialogTile(): void {
    this.shadeTools.simpleOkDialog(
      this.infoDialogContent.header,
      this.infoDialogContent.message,
    );
  }


  /**
   * Reusable generator of popover menu options for any configurable setting
   * that is a ModuleProperty
   *
   * @private
   */
  private makeTileMenuOptions(): OptionGroup {
    const menuOptions = [
      {
        id: 'exist',
        name: 'Choose from existing tiles',
        callback: () => {
          this.existingTilesMenuOpen = true;
        },
      },
      {
        id: 'new',
        name: 'Create a new tile',
        callback: this.callbackCreateTile,
      },
    ];

    if (this.moduleItem.TileID) {
      menuOptions.push(
        {
          id: 'conf',
          name: 'Configure this tile',
          callback: () => {
            this.editingTile = this.moduleItem.TileID;
          },
        },
        {
          id: 'rmv',
          name: 'Remove this tile',
          callback: () => {
            this.moduleItem.TileID = EMPTY_ID;
          },
        },
      );
    }

    return new OptionGroup(menuOptions);
  }


  onClickExistingTile($event: Option) {
    this.clickedExistingTile$.emit($event.id);
    this.existingTilesMenuOpen = false;
  }


  onClickOutsideExistingTiles() {
    this.existingTilesMenuOpen = false;
  }
}
